import { useState, useEffect } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Clock as ClockIcon, Moon as BedtimeIcon, GraduationCap as SchoolIcon, Sun as WeekendIcon } from 'lucide-react'
import { useOnboarding } from './OnboardingFlow'
import { polling } from '../../lib/polling'
import { api } from '../../lib/api'

export default function SchedulesStep() {
  const navigate = useNavigate()
  const { nextStep, validationError } = useOnboarding()
  const [hasPolicies, setHasPolicies] = useState(false)
  const [hasSchedules, setHasSchedules] = useState(false)
  const [checking, setChecking] = useState(true)

  useEffect(() => {
    const checkRequirements = async () => {
      try {
        const prerequisites = await api.getOnboardingPrerequisites()
        setHasPolicies(prerequisites.hasPolicies)
        setHasSchedules(prerequisites.hasSchedules)
      } catch {
        setHasPolicies(false)
        setHasSchedules(false)
      } finally {
        setChecking(false)
      }
    }

    checkRequirements()

    const interval = setInterval(checkRequirements, polling.onboardingCheck)
    return () => clearInterval(interval)
  }, [])

  const handleCreateSchedule = () => {
    navigate({ to: '/schedules', search: { new: true } })
  }

  const handleContinue = async () => {
    await nextStep()
  }

  const scheduleTypes = [
    { icon: <BedtimeIcon className="text-indigo-500" />, title: 'Bedtime', description: 'Set device downtime for better sleep' },
    { icon: <SchoolIcon className="text-green-500" />, title: 'School Hours', description: 'Limit distractions during school time' },
    { icon: <WeekendIcon className="text-orange-500" />, title: 'Weekend Rules', description: 'Different limits for weekends' },
  ]

  return (
    <div className="bg-white rounded-2xl shadow-xl p-8 sm:p-12">
      <div className="text-center mb-8">
        <div className="w-20 h-20 rounded-2xl bg-brand-light flex items-center justify-center mx-auto mb-6">
          <ClockIcon size={64} className="text-brand" />
        </div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">Create Schedules</h2>
        <p className="text-gray-600 max-w-md mx-auto">
          Set up time-based rules to automatically manage device access throughout the day.
        </p>
      </div>

      {validationError && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-xl p-4">
          <p className="text-sm text-red-700 text-center">{validationError}</p>
        </div>
      )}

      {!checking && !hasPolicies && (
        <div className="mb-6 bg-amber-50 border border-amber-200 rounded-xl p-4">
          <p className="text-sm text-amber-800 text-center">
            Please create at least one rule before creating schedules.
          </p>
        </div>
      )}

      {!checking && hasSchedules && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-xl p-4">
          <p className="text-sm text-green-800 text-center">
            Great! You have a schedule. Click continue to proceed.
          </p>
        </div>
      )}

      <div className="space-y-3 mb-8">
        {scheduleTypes.map((schedule, index) => (
          <div key={index} className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl">
            <div className="w-10 h-10 rounded-lg bg-white flex items-center justify-center shadow-sm">
              {schedule.icon}
            </div>
            <div>
              <h4 className="font-medium text-gray-900">{schedule.title}</h4>
              <p className="text-sm text-gray-500">{schedule.description}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="space-y-3">
        <button
          onClick={handleCreateSchedule}
          disabled={!hasPolicies}
          className="w-full py-3 bg-brand text-white rounded-xl font-medium hover:bg-brand-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Create Schedule Now
        </button>

        {hasSchedules && (
          <button
            onClick={handleContinue}
            className="w-full py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
          >
            Continue
          </button>
        )}
      </div>
    </div>
  )
}
